import moment from 'moment';

import { CompanyEquipmentType, EmployeeEquipmentType } from '~/data/equipment';

export enum EquipmentEntryType {
  Employee = 'employee',
  Company = 'company',
}

export type CommonEquipmentEntry = {
  id: number;
  type: string;
  item: string;
  inventoryNumber: string;
  attachmentId: number;
  attachmentName: string;
  attachmentIsArchived: boolean;
  expirationDate?: Date;
  attachment?: File;
};

export type CompanyEquipmentEntry = CommonEquipmentEntry & {
  entryType: EquipmentEntryType.Company;
};

export type EmployeeEquipmentEntry = CommonEquipmentEntry & {
  entryType: EquipmentEntryType.Employee;
  companyEquipmentId: number;
  deliveryDate: Date;
  returnDate?: Date;
  info?: string;
};

export type EquipmentEntry = CompanyEquipmentEntry | EmployeeEquipmentEntry;

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type EmployeeEquipmentTableRow = PartialBy<
  EmployeeEquipmentEntry,
  'deliveryDate'
>;

export const convertCompanyEquipmentTypeToEquipmentEntry = (
  data: CompanyEquipmentType,
): CompanyEquipmentEntry => {
  const {
    fComEquipmentId,
    fComEquipmentType,
    fComEquipmentItem,
    fComEquipmentInvNumber,
    fComEquipmentAttachmentId,
    fComEquipmentAttachmentName,
    fComEquipmentAttachmentIsArchived,
    fComEquipmentExpirationDate,
  } = data;

  return {
    id: fComEquipmentId,
    entryType: EquipmentEntryType.Company,
    type: fComEquipmentType,
    item: fComEquipmentItem,
    inventoryNumber: fComEquipmentInvNumber,
    attachmentId: fComEquipmentAttachmentId,
    attachmentName: fComEquipmentAttachmentName,
    attachmentIsArchived: fComEquipmentAttachmentIsArchived,
    expirationDate: !!fComEquipmentExpirationDate
      ? moment(fComEquipmentExpirationDate).toDate()
      : undefined,
  };
};

export const convertEmployeeEquipmentTypeToEquipmentEntry = (
  data: EmployeeEquipmentType,
): EmployeeEquipmentEntry => {
  const {
    fEquipmentId,
    fEquipmentCompEqFK,
    fEquipmentType,
    fEquipmentItem,
    fEquipmentInvNumber,
    fEquipmentDeliveryDate,
    fEquipmentAttachmentId,
    fEquipmentAttachmentName,
    fEquipmentAttachmentIsArchived,
    fEquipmentReturnDate,
    fEquipmentInfo,
    fEquipmentExpirationDate,
  } = data;

  return {
    id: fEquipmentId,
    entryType: EquipmentEntryType.Employee,
    companyEquipmentId: fEquipmentCompEqFK,
    type: fEquipmentType,
    item: fEquipmentItem,
    inventoryNumber: fEquipmentInvNumber,
    deliveryDate: moment(fEquipmentDeliveryDate).toDate(),
    attachmentId: fEquipmentAttachmentId,
    attachmentName: fEquipmentAttachmentName,
    attachmentIsArchived: fEquipmentAttachmentIsArchived,
    returnDate: !!fEquipmentReturnDate
      ? moment(fEquipmentReturnDate).toDate()
      : undefined,
    info: fEquipmentInfo,
    expirationDate: !!fEquipmentExpirationDate
      ? moment(fEquipmentExpirationDate).toDate()
      : undefined,
  };
};

export const convertEmployeeEquipmentTableRowToSubmission = (
  tableRow: EmployeeEquipmentTableRow,
): EmployeeEquipmentType => {
  const {
    id,
    companyEquipmentId,
    type,
    item,
    inventoryNumber,
    deliveryDate,
    returnDate,
    info,
    expirationDate,
    attachmentId,
    attachmentName,
    attachmentIsArchived,
    attachment,
  } = tableRow;

  const convertDateString = (date?: Date) => {
    return !!date ? moment(date).format('YYYY-MM-DD') : '';
  };

  return {
    fEquipmentId: id,
    fEquipmentCompEqFK: companyEquipmentId,
    fEquipmentType: type,
    fEquipmentItem: item,
    fEquipmentInvNumber: inventoryNumber,
    fEquipmentDeliveryDate: convertDateString(deliveryDate),
    fEquipmentReturnDate: convertDateString(returnDate),
    fEquipmentInfo: info,
    fEquipmentExpirationDate: convertDateString(expirationDate),
    fEquipmentAttachmentId: attachmentId,
    fEquipmentAttachmentName: attachmentName,
    fEquipmentAttachmentIsArchived: attachmentIsArchived,
    fEquipmentAttachment: attachment,
  };
};

export const employeeEquipmentSubmissionToFormData = (
  eq: EmployeeEquipmentType,
) => {
  const {
    fEquipmentId,
    fEquipmentType,
    fEquipmentAttachmentId,
    fEquipmentAttachmentName,
    fEquipmentAttachmentIsArchived,
    fEquipmentCompEqFK,
    fEquipmentItem,
    fEquipmentInfo,
    fEquipmentExpirationDate,
    fEquipmentDeliveryDate,
    fEquipmentReturnDate,
    fEquipmentInvNumber,
    fEquipmentAttachment,
  } = eq;

  const formData = new FormData();
  formData.append('fEquipmentId', fEquipmentId.toString());
  formData.append('fEquipmentType', fEquipmentType);
  formData.append('fEquipmentItem', fEquipmentItem ?? '');
  formData.append('fEquipmentInvNumber', fEquipmentInvNumber);
  formData.append('fEquipmentReturnDate', fEquipmentReturnDate ?? '');
  formData.append('fEquipmentInfo', fEquipmentInfo ?? '');

  formData.append('fEquipmentDeliveryDate', fEquipmentDeliveryDate);
  formData.append(
    'fEquipmentAttachmentId',
    fEquipmentAttachmentId ? fEquipmentAttachmentId.toString() : '-1',
  );
  formData.append('fEquipmentAttachmentName', fEquipmentAttachmentName || '');
  formData.append(
    'fEquipmentAttachmentIsArchived',
    fEquipmentAttachmentIsArchived ? 'true' : 'false',
  );
  formData.append('fEquipmentExpirationDate', fEquipmentExpirationDate ?? '');
  formData.append(
    'fEquipmentCompEqFK',
    fEquipmentCompEqFK ? fEquipmentCompEqFK.toString() : '-1',
  );
  if (fEquipmentAttachment) {
    formData.append('file', fEquipmentAttachment);
  }
  return formData;
};
